/**
 * @file AbaPaymentTracker.jsx
 * @description Standalone SEO page at /aba-payment-tracker — targets "ABA
 *   payment tracker" / "RBT pay tracker" / "track unpaid ABA sessions"
 *   search intent. Focused on the money side of the free ABAcases tracker:
 *   what was worked vs. what was paid vs. what remains unpaid, across
 *   multiple agencies. Structural/stylistic template: AboutAbaCases.jsx.
 *   Visible copy is in the `abaPaymentTracker.*` / `trackerSeoPages.*` i18n
 *   namespaces (EN + ES, runtime switch only); the SEO surface stays English.
 * @module components/AbaPaymentTracker
 */
import { useEffect, useState } from "react";
import { T } from "../theme";
import {
  DollarIcon, CheckCircleIcon, AlertTriangleIcon, BuildingIcon, ClockIcon,
  DownloadIcon, NoteIcon, CarIcon, BellIcon,
} from "./Icons";
import PublicFooter from "./PublicFooter";
import QuietEnding from "./QuietEnding";
import { HowItWorksStrip, IconTiles, MoreDetails, DetailList } from "./TrackerVisuals";
import StickyMobileCta from "./StickyMobileCta";
import { setSignupIntent } from "../lib/signupIntent";
import { useBreadcrumbJsonLd, useSiteSchema } from "../lib/seoSchema";
import { usePageSeo, seoHref } from "../lib/seo";
import { track, A, ctaMeta } from "../lib/analytics";
import { afterLink } from "../lib/linkText";
import i18n from "../i18n";
import { useTranslation } from "react-i18next";
import Breadcrumbs from "./Breadcrumbs";

const NAVY = "#0F1024";
const NAVY_MID = "#1a1f3c";
const INDIGO = "#4F46E5";
const INDIGO_LT = "#818cf8";
const BORDER = "rgba(226,232,240,0.8)";
const TEXT_MID = "#475569";

// Card grids: shown as icon tiles, with the long write-ups collapsed under "Details" (kept in the HTML for search).
const GRID_1 = [
  { Icon: CheckCircleIcon, titleKey: "abaPaymentTracker.status1Title", bodyKey: "abaPaymentTracker.status1Body" },
  { Icon: AlertTriangleIcon, titleKey: "abaPaymentTracker.status2Title", bodyKey: "abaPaymentTracker.status2Body" },
  { Icon: DollarIcon, titleKey: "abaPaymentTracker.status3Title", bodyKey: "abaPaymentTracker.status3Body" },
];

const GRID_2 = [
  { Icon: ClockIcon, titleKey: "abaPaymentTracker.track1Title", bodyKey: "abaPaymentTracker.track1Body" },
  { Icon: BuildingIcon, titleKey: "abaPaymentTracker.track2Title", bodyKey: "abaPaymentTracker.track2Body" },
  { Icon: CarIcon, titleKey: "abaPaymentTracker.track3Title", bodyKey: "abaPaymentTracker.track3Body" },
  { Icon: DollarIcon, titleKey: "abaPaymentTracker.track4Title", bodyKey: "abaPaymentTracker.track4Body" },
  { Icon: DownloadIcon, titleKey: "abaPaymentTracker.track5Title", bodyKey: "abaPaymentTracker.track5Body" },
  { Icon: NoteIcon, titleKey: "abaPaymentTracker.track6Title", bodyKey: "abaPaymentTracker.track6Body" },
];

export default function AbaPaymentTracker({ onNavigate }) {
  const { t } = useTranslation();
  const isEs = String(i18n.resolvedLanguage || i18n.language || "").toLowerCase().startsWith("es");
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 760 : false));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // The SEO surface (title, description, canonical, JSON-LD) stays English; only the visible copy follows the language switcher.
  usePageSeo({
    title: "ABA Payment Tracker for Hours and Unpaid Balances | ABAcases",
    description: "Track ABA payments, unpaid sessions, agency payments, cancellations, mileage, and expenses with ABAcases' free payment tracker for ABA providers.",
    canonical: "/aba-payment-tracker",
  });
  useSiteSchema(); // global Organization + WebSite
  useBreadcrumbJsonLd("bc-jsonld-aba-payment-tracker", [
    { name: "Home", url: "/" },
    { name: "Free ABA Timesheet & Payment Tracker", url: "/free-aba-timesheet-payment-tracker" },
    { name: "ABA Payment Tracker", url: "/aba-payment-tracker" },
  ]);

  // Crawlable internal link (real <a href> so Googlebot follows it; preventDefault
  // keeps SPA nav). Same pattern as AboutAbaCases / CityRolePage.
  const SeoLink = ({ to, style, children }) => (
    <a href={seoHref(to)} onClick={(e) => { e.preventDefault(); onNavigate(to); }} style={{ textDecoration: "none", ...style }}>{children}</a>
  );

  const goJoin = () => {
    track(A.TRACKER_SIGNUP_CLICK, {
      supabaseMeta: { source: "aba_timesheet" },
      ga4Params: ctaMeta({ pagePath: "/aba-payment-tracker/", pageType: "tracker", ctaLabel: "Start tracking free", targetPath: "/auth", userLanguage: i18n.language, audience: "tracker" }),
    });
    setSignupIntent("tracker");
    onNavigate("auth");
  };

  const sectionPad = isMobile ? "44px 18px" : "64px 48px";
  const maxW = 900;

  return (
    <div lang={isEs ? "es" : "en"} style={{ flex: 1, overflowY: "auto", background: "#fff", fontFamily: T.fontBody }}>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <header style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`, padding: isMobile ? "28px 18px 44px" : "32px 48px 64px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -120, right: "10%", width: 420, height: 420, borderRadius: "50%", background: "rgba(79,70,229,0.16)", filter: "blur(90px)", pointerEvents: "none" }} />
        <div style={{ position: "relative", maxWidth: maxW, margin: "0 auto" }}>
          {/* Breadcrumb */}
          <Breadcrumbs items={[
            { label: t("trackerSeoPages.crumbHome"), onClick: () => onNavigate("home") },
            { label: t("trackerSeoPages.crumbTracker"), onClick: () => onNavigate("free-aba-timesheet-payment-tracker") },
            { label: t("abaPaymentTracker.crumbCurrent") },
          ]} />

          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 11.5, fontWeight: 800, color: INDIGO_LT, background: "rgba(255,255,255,0.1)", borderRadius: 7, padding: "4px 10px", letterSpacing: "0.02em" }}>{t("abaPaymentTracker.badge")}</span>
            <span style={{ fontSize: 12, color: INDIGO_LT, fontWeight: 600 }}>abacases.com</span>
          </div>

          <h1 style={{ fontSize: isMobile ? 25 : 40, fontWeight: 900, color: "#fff", margin: "0 0 12px", lineHeight: 1.14, letterSpacing: "-0.025em", maxWidth: 760 }}>
            {t("abaPaymentTracker.h1")}
          </h1>
          <p style={{ fontSize: isMobile ? 16 : 19, fontWeight: 600, color: "#e2e8f0", lineHeight: 1.4, maxWidth: 700, margin: "0 0 16px" }}>
            {t("providerPages.trackerLead")}
          </p>
          <p style={{ fontSize: isMobile ? 15 : 16.5, color: "#cbd5e1", lineHeight: 1.65, maxWidth: 660, margin: "0 0 28px" }}>
            {t("abaPaymentTracker.heroBefore")}{" "}
            <SeoLink to="free-aba-timesheet-payment-tracker" style={{ color: "#fff", fontWeight: 700, borderBottom: "1px solid rgba(255,255,255,0.4)" }}>
              {t("abaPaymentTracker.heroLink")}
            </SeoLink>
            {afterLink(t("abaPaymentTracker.heroAfter"))}
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button onClick={goJoin} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: isMobile ? "13px 24px" : "15px 30px", background: T.gradientIndigo, color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: "pointer", boxShadow: "0 6px 24px rgba(79,70,229,0.4)" }}>
              <BellIcon size={16} color="#fff" /> {t("trackerSeoPages.startTracking")}
            </button>
          </div>
        </div>
      </header>

      {/* ── How it works: log, pay date, flagged if late ──────────── */}
      <section style={{ padding: isMobile ? "36px 16px 8px" : "52px 48px 8px" }}>
        <HowItWorksStrip isMobile={isMobile} />
      </section>

      {/* ── 1. Worked, paid, and unpaid can quietly drift apart ─── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 23 : 30, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t("abaPaymentTracker.driftTitle")}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t("abaPaymentTracker.driftP1")}
          </p>
          <MoreDetails summary={t("trackerVisual.readMore")}>
            <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
              {t("abaPaymentTracker.driftP2")}
            </p>
          </MoreDetails>
        </div>
      </section>

      {/* ── 2. Paid vs. unpaid visibility, per agency ─────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 22px", letterSpacing: "-0.02em" }}>
            {t("abaPaymentTracker.statusTitle")}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 20 }}>
            {t("abaPaymentTracker.statusP")}
          </p>
          <IconTiles isMobile={isMobile} columns={3} items={GRID_1.map((c) => ({ Icon: c.Icon, title: t(c.titleKey) }))} />
          <MoreDetails summary={t("trackerVisual.details")}>
            <DetailList items={GRID_1.map((c) => ({ title: t(c.titleKey), body: t(c.bodyKey) }))} />
          </MoreDetails>
        </div>
      </section>

      {/* ── 3. Not a payroll system — your own record ─────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t("abaPaymentTracker.recordTitle")}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t("abaPaymentTracker.recordP1")}
          </p>
          <MoreDetails summary={t("trackerVisual.readMore")}>
            <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
              {t("abaPaymentTracker.recordP2")}
            </p>
          </MoreDetails>
        </div>
      </section>

      {/* ── 4. What you can track ──────────────────────────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 22px", letterSpacing: "-0.02em" }}>
            {t("abaPaymentTracker.trackTitle")}
          </h2>
          <div style={{ marginBottom: 18 }}>
            <IconTiles isMobile={isMobile} columns={3} items={GRID_2.map((c) => ({ Icon: c.Icon, title: t(c.titleKey) }))} />
            <MoreDetails summary={t("trackerVisual.details")}>
              <DetailList items={GRID_2.map((c) => ({ title: t(c.titleKey), body: t(c.bodyKey) }))} />
            </MoreDetails>
          </div>
          <p style={{ fontSize: 15, color: NAVY, fontWeight: 600, lineHeight: 1.65 }}>
            {t("abaPaymentTracker.noteBefore")}{" "}
            <SeoLink to="aba-timesheet" style={{ color: INDIGO, fontWeight: 700 }}>{t("abaPaymentTracker.noteLinkAba")}</SeoLink>{afterLink(t("abaPaymentTracker.noteMid"))}{" "}
            <SeoLink to="rbt-timesheet" style={{ color: INDIGO, fontWeight: 700 }}>{t("abaPaymentTracker.noteLinkRbt")}</SeoLink>{afterLink(t("abaPaymentTracker.noteAfter"))}
          </p>
        </div>
      </section>

      {/* ── 5. Why this matters across multiple agencies ──────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t("abaPaymentTracker.multiTitle")}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t("abaPaymentTracker.multiP1")}
          </p>
          <MoreDetails summary={t("trackerVisual.readMore")}>
            <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
              {t("abaPaymentTracker.multiP2")}
            </p>
          </MoreDetails>
          <p style={{ fontSize: 14, color: TEXT_MID, lineHeight: 1.7 }}>
            {t("abaPaymentTracker.multiP3Before")}{" "}
            <SeoLink to="how-to-choose-an-aba-case" style={{ color: INDIGO, fontWeight: 700 }}>{t("abaPaymentTracker.multiP3Link")}</SeoLink>.
          </p>
        </div>
      </section>

      {/* One quiet ending instead of a second CTA band: the hero is this page's only call to action. */}
      <QuietEnding isMobile={isMobile} />

      <PublicFooter />
      <StickyMobileCta label={t("stickyCta.startTracking")} onClick={goJoin} ctaId="tracker" />
    </div>
  );
}
